import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography, SubmitLoaderButton } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  openAdminModal,
  closeAdminModal,
} from "store/slices/common/adminModal.slice";
import { openToast } from "store/slices/common/toast.slice";
import { Button } from "components/ui";
import { EllipsisVertical, Pencil, Trash, Archive } from "lucide-react";

// import { DeleteMaintenanceAction } from "store/middlewares/maintenance";
import checkAccess from "utils/checkAccess";
import dayjs from "dayjs";
import ModalEditMaintenance from "components/molecules/DataManagement/ModalEditMaintenance";
import { DeleteMaintenanceAction, ArchiveMaintenanceAction } from "store/middlewares/settings/settings";

/* ---------- delete confirm (same file) ---------- */
const DeleteMaintenanceConfirm = ({ row, filter }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = () => {
    setIsSubmitting(true);

    // ---- API (uncomment when backend is ready, then delete the dummy block) ----
    dispatch(
      DeleteMaintenanceAction({
        id: row.id,
        filter,
        onSuccess: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
        onError: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
      }),
    );

   
    // ---- End dummy block ----
  };

  return (
    <div className="grid gap-5 p-1">
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
          <Trash className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          Delete this maintenance?
        </h2>
      </div>

      <ul className="grid gap-2">
        <li className="flex gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>
            The maintenance scheduled from{" "}
            <strong>
              {dayjs(Number(row.starts_at)).format("DD/MM/YYYY, hh:mm A")}
            </strong>{" "}
            to{" "}
            <strong>
              {dayjs(Number(row.ends_at)).format("DD/MM/YYYY, hh:mm A")}
            </strong>{" "}
            will be permanently removed from the system.
          </span>
        </li>
        <li className="flex gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          This action cannot be undone.
        </li>
      </ul>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={isSubmitting}
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          type="button"
          variant="destructive"
          size="lg"
          btnText="Delete maintenance"
          isDisable={isSubmitting}
          onClick={handleDelete}
        />
      </div>
    </div>
  );
};
/* ----------------------------------------------- */

const MaintenanceActionBar = ({ row, filter }) => {
  const dispatch = useDispatch();

  const ActionMenuArray = [
    // Show Edit and Delete only for scheduled status
    row.status === "scheduled" && checkAccess("update_maintenance") && {
      label: "Edit",
      icon: <Pencil size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            content: <ModalEditMaintenance row={row} filter={filter} />,
            maxWidth: "900px",
          }),
        );
      },
    },
    row.status === "scheduled" && checkAccess("delete_maintenance") && {
      label: "Delete",
      icon: <Trash size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            title: "",
            content: <DeleteMaintenanceConfirm row={row} filter={filter} />,
          }),
        );
      },
    },
    // Show Archive only for completed status
    row.status === "completed" && checkAccess("update_maintenance") && {
      label: "Archive",
      icon: <Archive size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          ArchiveMaintenanceAction({
            id: row.id,
            filter,
            onSuccess: () => {
              dispatch(openToast({ type: "success", message: "Maintenance archived successfully" }));
            },
            onError: () => {
              dispatch(openToast({ type: "error", message: "Failed to archive maintenance" }));
            },
          }),
        );
      },
    },
  ].filter(Boolean);

  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3">
      <Button
        className="size-7"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default MaintenanceActionBar;